import React, { useEffect, useState } from "react";
import { Alert, Button, DatePicker, Descriptions, InputNumber, Modal, Space, Switch, Tag, message } from "antd";
import { SaveOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import moment from "moment";
import { ApiResponse } from "enums";
import { BloqueoMovimientosService } from "services/VacacionesService";

const FORMATO_VISUAL = "DD/MM/YYYY";
const FORMATO_API = "YYYY-MM-DD";

const BloqueoMovimientosPage = () => {
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [configuracion, setConfiguracion] = useState({});
  const [activo, setActivo] = useState(false);
  const [fechaLimite, setFechaLimite] = useState(null);
  const [diasRetroactivos, setDiasRetroactivos] = useState(null);

  const aplicarConfiguracion = (data) => {
    setConfiguracion(data || {});
    setActivo(!!data?.activo);
    setFechaLimite(data?.fechaLimite ? moment(data.fechaLimite, FORMATO_API) : null);
    setDiasRetroactivos(data?.diasRetroactivos ?? null);
  };

  useEffect(() => {
    const cargar = async () => {
      try {
        const respuesta = await BloqueoMovimientosService.obtener();
        if (respuesta.estado === ApiResponse.EXITO) {
          aplicarConfiguracion(respuesta.data);
        } else {
          message.error(respuesta.mensaje);
        }
      } catch (error) {
        message.error("No se pudo cargar la configuración del bloqueo de movimientos.");
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      const respuesta = await BloqueoMovimientosService.actualizar({
        activo,
        fechaLimite: fechaLimite ? fechaLimite.format(FORMATO_API) : null,
        diasRetroactivos,
      });
      if (respuesta.estado === ApiResponse.EXITO) {
        aplicarConfiguracion(respuesta.data);
        message.success("Se guardó la configuración del bloqueo de movimientos.");
      } else {
        message.error(respuesta.mensaje);
      }
    } catch (error) {
      message.error("No se pudo guardar la configuración del bloqueo de movimientos.");
    } finally {
      setGuardando(false);
    }
  };

  const onClickGuardar = () => {
    if (activo && !fechaLimite) {
      message.warning("Seleccione la fecha límite para activar el bloqueo.");
      return;
    }
    if (!activo) {
      guardar();
      return;
    }
    Modal.confirm({
      title: "Confirmar bloqueo de movimientos",
      content: `A partir de ahora no se podrán solicitar ni aprobar movimientos con fecha de inicio hasta el ${fechaLimite.format(
        FORMATO_VISUAL
      )} (inclusive). Las solicitudes pendientes en ese periodo solo podrán rechazarse.`,
      okText: "Guardar",
      cancelText: "Cancelar",
      onOk: guardar,
    });
  };

  const vigente = !!configuracion.fechaLimiteVigente;

  return (
    <div style={{ maxWidth: 720 }}>
      <Alert
        showIcon
        type="info"
        style={{ marginBottom: 16 }}
        message="Bloqueo de movimientos por fecha límite"
        description="Mientras esté activo, el portal del colaborador no permite solicitar movimientos, y las jefaturas no pueden aprobarlos, si su fecha de inicio es menor o igual a la fecha límite. Por ejemplo, con fecha límite 05/10/2026 solo se podrá solicitar y aprobar desde el 06/10/2026. Al desactivarlo todo vuelve a funcionar con normalidad; al cambiar la fecha se respeta la nueva fecha de inmediato. Este bloqueo no aplica a las acciones realizadas desde el administrador."
      />

      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space>
          <Switch checked={activo} onChange={setActivo} loading={cargando} />
          <span>Activar bloqueo de movimientos</span>
          {!cargando && (
            <Tag icon={vigente ? <LockOutlined /> : <UnlockOutlined />} color={vigente ? "error" : "default"}>
              {vigente
                ? `Bloqueado hasta el ${moment(configuracion.fechaLimiteVigente, FORMATO_API).format(FORMATO_VISUAL)}`
                : "Sin bloqueo vigente"}
            </Tag>
          )}
        </Space>

        <Space>
          <span>Fecha límite (inclusive):</span>
          <DatePicker
            value={fechaLimite}
            onChange={setFechaLimite}
            format={FORMATO_VISUAL}
            placeholder="Seleccione la fecha"
            disabled={cargando}
          />
        </Space>

        <Space>
          <span>Días hacia atrás permitidos al solicitar:</span>
          <InputNumber
            min={0}
            max={365}
            precision={0}
            value={diasRetroactivos}
            onChange={setDiasRetroactivos}
            placeholder="Sin límite"
            disabled={cargando}
          />
        </Space>
        <Alert
          showIcon
          type="info"
          message="Con 1 día, si hoy es 21 solo se podrá solicitar con fecha de inicio 20 y 21 en adelante. Déjelo vacío para no limitar. Es independiente del bloqueo por fecha límite y no aplica a los tipos de movimiento que permiten solicitar fechas anteriores. No afecta la aprobación. Si el bloqueo por fecha límite está activo, ese manda y esta regla no se aplica."
        />

        <Button type="primary" icon={<SaveOutlined />} onClick={onClickGuardar} loading={guardando} disabled={cargando}>
          Guardar
        </Button>

        {configuracion.fechaActualizacion && (
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="Última actualización">
              {moment(configuracion.fechaActualizacion).format("DD/MM/YYYY HH:mm")}
              {configuracion.usuarioActualizacionNumeroDocumentoIdentidad
                ? ` (usuario ${configuracion.usuarioActualizacionNumeroDocumentoIdentidad})`
                : ""}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Space>
    </div>
  );
};

export default BloqueoMovimientosPage;
